import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Clean gutters and flush six downspouts, 180 linear ft, two-storey ranch addition', amount: '$240' },
  { item: 'Reseal two leaking end caps and one miter, re-pitch a sagging 12 ft section', amount: '$135' },
  { item: 'Bag all debris and haul it away (no piles left in the beds)', amount: '$25' },
  { item: 'Optional: aluminum micro-mesh guards, 180 linear ft, installed', amount: '$1,620' },
];

const faq = [
  {
    q: 'Can I offer gutter guards on the same quote as a cleaning?',
    a: 'Yes, and it is the best place to do it. Add the guards as an optional line in Scopie. The homeowner sees the cleaning price and the guard price together, ticks the guards if they want them, and the total recalculates before they sign. If they come back to the link later, their choice is still there.',
  },
  {
    q: 'How do gutter companies price a cleaning?',
    a: 'Usually by linear foot with an adjustment for storeys, roof pitch and how clogged the system is, plus downspout flushing. Repairs and guards are priced separately so the homeowner can choose them.',
  },
  {
    q: 'What should I say about fascia and soffit damage?',
    a: 'That rot found behind the gutters is not part of the cleaning and will be quoted before any repair. Homeowners often discover fascia problems only when a gutter is pulled away.',
  },
  {
    q: 'Does Scopie remind customers when the next cleaning is due?',
    a: 'No. There are no automatic reminders or recurring jobs yet. Past customers and their quotes stay in your client list, so you can look them up when fall comes round.',
  },
];

const page: IndustryPage = {
  slug: 'gutter-cleaning',
  order: 10,
  group: 'Outdoor',
  path: '/for/gutter-cleaning',
  primaryKeyword: 'gutter cleaning business software',
  secondaryKeywords: ['gutter cleaning quote template', 'gutter guard quote', 'gutter installation estimate'],
  title: 'Gutter Cleaning Software with Guard Upsells | Scopie', // 52
  metaDescription:
    'Quote gutter cleaning by the foot, list repairs on their own lines, and offer guards as an optional add-on the homeowner can tick on the quote page.', // 147
  h1: 'Gutter quotes that turn a cleaning into a guard install',
  answerFirst:
    'Scopie gives gutter companies a quote where the cleaning, the repairs you spotted from the ladder and an optional set of guards sit side by side. The homeowner ticks the guards if they want them and the total updates on the spot. Bigger installs carry your deposit rule, and debris and roof terms are spelled out.',
  exampleQuote: { label: 'Sample gutter quote', lines, total: '$2,020', depositNote: 'Install deposits and roof-access terms are the company\'s own clauses.' },
  blocks: [
    {
      type: 'text',
      id: 'ladder',
      h2: 'You see the problems from the ladder. The homeowner never does.',
      lead: 'Two leaking corners, a sagging run and enough maple seeds to fill a wheelbarrow. They only asked for a clean.',
      paragraphs: [
        'Most gutter repair and guard work is found during a cleaning, and most of it is lost because it is mentioned in passing and never priced. Put what you saw on the quote: each repair as its own line, and guards as an optional extra with the footage and the product named.',
        'A homeowner who would never call for guards will often add them when the price is right there next to the cleaning they already agreed to.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'Cleaning, repairs and an optional guard install',
      label: 'Sample quote: 180 ft of gutter, two storeys',
      lines,
      total: '$2,020',
      totalLabel: 'Total if the guards are ticked',
      note: 'Example figures. Without the guards, the homeowner signs for the cleaning, repairs and haul-away only.',
    },
    {
      type: 'list',
      id: 'season',
      h2: 'Two busy seasons, two different jobs',
      style: 'bullets',
      items: [
        { title: 'Late fall', text: 'After the leaves drop: full cleanings, downspout clogs and the first guard conversations.' },
        { title: 'Spring', text: 'Seed pods, shingle grit and winter damage: re-pitching, end caps and seams pulled apart by ice.' },
        { title: 'Summer', text: 'Seamless replacement and guard installs, when roofs are dry and crews have time.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Gutter quoting mistakes',
      style: 'mistakes',
      items: [
        { title: 'Guards mentioned, not priced', text: 'A sentence on the invoice sells nothing. An optional line with a number does.' },
        { title: 'Debris left unsaid', text: 'Bagged and removed, or left in a pile? Homeowners notice.' },
        { title: 'Roof walking not addressed', text: 'Say when you walk the roof, when you will not, and that steep or wet roofs may need a different day.' },
        { title: 'Seamless runs quoted without a deposit', text: 'Coil and hangers are bought for that house. Put your deposit rule on install quotes.' },
        { title: 'Downspout layout guessed', text: 'For new gutters, write where each downspout goes and where the water ends up.' },
      ],
    },
    {
      type: 'text',
      id: 'crew',
      h2: 'Tall ladders, one gutter machine, several crews',
      paragraphs: [
        'Signed jobs go on the schedule with the crew and the equipment they need, so the seamless machine and the 32-foot ladders are planned for the right driveway. Crews see where they are headed, not what the homeowner is paying. Afterwards, the invoice is a link you can text before you load the ladders.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Gutter cleaning and guard questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/for/window-cleaning', anchor: 'Scopie for window cleaning businesses', status: 'live' },
    { href: '/templates/gutter-cleaning-quote-template', anchor: 'free gutter cleaning quote template', status: 'planned' },
  ],
  cta: {
    heading: 'Price the guards before you climb down',
    text: 'Keep your per-foot prices and guard options in Scopie and add them to every cleaning quote.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
